import { useEffect, useState } from 'react';
import { useStudioList, useTeamList } from '@/api/base';
import { TextField } from '@/components/kit/Field';
import { FieldHint, OptionList } from '@/components/kit/OptionList';

export function useDebounced(value: string, ms = 250): string {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

/**
 * Выбор студии в диалоге (studioRadios в макете): поиск и до пяти
 * вариантов списком под полем; «Без студии» — всегда (БЛ 3.3).
 */
export function StudioPicker({
  value,
  onChange,
  allowNone,
  label = 'Студия',
}: {
  value: { id: string; name: string } | null;
  onChange: (value: { id: string; name: string } | null) => void;
  allowNone?: boolean;
  label?: string;
}) {
  const [text, setText] = useState('');
  const q = useDebounced(text);
  const list = useStudioList({ q, limit: 5 });
  const items = list.data?.items ?? [];
  const NONE = '__none';
  const options = [
    ...(value && !items.some((s) => s.id === value.id)
      ? [{ value: value.id, title: value.name, description: 'выбрана' }]
      : []),
    ...items.map((s) => ({
      value: s.id,
      title: s.name,
      description: [s.city.name, s.mainContact ? null : 'контактов нет'].filter(Boolean).join(' · '),
    })),
    ...(allowNone ? [{ value: NONE, title: 'Без студии', description: 'команда сама по себе' }] : []),
  ];
  return (
    <>
      <TextField label={label} placeholder="название или город" value={text} onValue={setText} />
      {q && !items.length && !list.isFetching ? <FieldHint gap>Студию не нашли.</FieldHint> : null}
      <OptionList
        value={value?.id ?? (allowNone ? NONE : null)}
        onChange={(id) => {
          if (id === NONE) return onChange(null);
          const s = items.find((x) => x.id === id);
          if (s) onChange({ id: s.id, name: s.name });
        }}
        options={options}
      />
    </>
  );
}

/** Выбор команды в диалоге «Включить в команду» (fToTeam в макете). */
export function TeamPicker({
  value,
  onChange,
  exclude = [],
}: {
  value: string | null;
  onChange: (id: string | null) => void;
  exclude?: string[];
}) {
  const [text, setText] = useState('');
  const q = useDebounced(text);
  const list = useTeamList({ q, limit: 6 });
  const items = (list.data?.items ?? []).filter((t) => !exclude.includes(t.id));
  return (
    <>
      <TextField label="Команда" placeholder="название, город или студия" value={text} onValue={setText} autoFocus />
      {!items.length && !list.isFetching ? <FieldHint gap>Ничего не нашли.</FieldHint> : null}
      <OptionList
        value={value}
        onChange={onChange}
        options={items.map((t) => ({
          value: t.id,
          title: t.name,
          description: [t.city.name, t.studio ? `студия ${t.studio.name}` : 'без студии', `${t.membersCount} в составе`].join(' · '),
        }))}
      />
    </>
  );
}
